import { format } from "date-fns";
import { ActivityRepository } from "../interfaces";
import { ServiceRecord, ServiceRenderedRecord } from "../types";
import { InMemoryStore, nextId } from "./store";
import { roundToPence } from "../../utils/money";

export class InMemoryActivityRepository implements ActivityRepository {
  constructor(private store: InMemoryStore) {}

  async listServices(): Promise<ServiceRecord[]> {
    return [...this.store.services];
  }

  async saveService(service: Partial<ServiceRecord>): Promise<ServiceRecord> {
    const created: ServiceRecord = {
      name: service.name ?? "",
      cost: service.cost ?? 0,
      ...service,
      id: nextId("service"),
      editable: false,
    };
    this.store.services.push(created);
    return created;
  }

  async updateService(service: ServiceRecord): Promise<void> {
    const idx = this.store.services.findIndex((s) => s.id === service.id);
    if (idx < 0) throw new Error("Failed to find service to update");
    this.store.services[idx] = { ...this.store.services[idx], ...service };
  }

  async deleteService(id: string): Promise<void> {
    this.store.services = this.store.services.filter((s) => s.id !== id);
  }

  async getRenderedForInvoice(invoiceId: string): Promise<ServiceRenderedRecord[]> {
    return this.store.servicesRendered.filter((s) => s.invoiceID === invoiceId);
  }

  async saveRendered(
    date: Date,
    clientId: string,
    serviceId: string,
    mileage: string,
    quantity: string,
    mileageCost: number
  ): Promise<void> {
    const service = this.store.services.find((s) => s.id === serviceId);
    const client = this.store.clients.find((c) => c.id === clientId);
    if (!client || !service) throw new Error("Unable to find client or service for this activity");

    this.store.servicesRendered.push({
      id: nextId("rendered"),
      date: format(date, "yyyy-MM-dd"),
      quantity: Number(quantity),
      mileage: parseFloat(mileage),
      mileageCost: roundToPence(mileageCost),
      serviceCost: service.cost,
      clientId: client.id,
      serviceId: service.id,
      serviceName: service.name,
    });
  }

  async updateRendered(
    id: string,
    date: string,
    clientId: string,
    serviceId: string,
    mileage: string,
    quantity: string,
    mileageCost: number,
    serviceCost: number
  ): Promise<void> {
    const idx = this.store.servicesRendered.findIndex((s) => s.id === id);
    if (idx < 0) throw new Error("Failed to find service rendered to update");
    const service = this.store.services.find((s) => s.id === serviceId);
    this.store.servicesRendered[idx] = {
      ...this.store.servicesRendered[idx],
      date,
      serviceId,
      serviceName: service?.name ?? this.store.servicesRendered[idx].serviceName,
      mileage: parseFloat(mileage),
      quantity: Number(quantity),
      mileageCost: roundToPence(mileageCost),
      serviceCost,
    };
  }

  async deleteRendered(id: string): Promise<void> {
    this.store.servicesRendered = this.store.servicesRendered.filter((s) => s.id !== id);
  }
}
